// ============================================================================
// Shell Type Definitions
// Feature: 035-app-shell-enhancement
// Author: Peter Dobler
// Created: 2026-01-21
// Description: TypeScript interfaces and types for the Application Shell
//              Enhancement feature including user preferences, search,
//              notifications, breadcrumbs, and keyboard shortcuts.
// ============================================================================

// =============================================================================
// Theme Types
// =============================================================================

/**
 * Theme mode options
 * - 'light': Force light theme
 * - 'dark': Force dark theme
 * - 'system': Follow OS preference
 */
export type ThemeMode = 'light' | 'dark' | 'system';

/**
 * Landing page options for user default landing page preference
 */
export type LandingPage = 'dashboard' | 'recent-activity' | 'favorites' | 'last-visited';

// =============================================================================
// User Preferences
// =============================================================================

/**
 * Favorite item structure for user favorites
 */
export interface FavoriteItem {
  /** Unique identifier for the favorite */
  id: string;
  /** Display title of the favorite */
  title: string;
  /** Navigation path */
  path: string;
  /** Category/section for grouping */
  category: string;
  /** When the favorite was added */
  addedAt: string;
}

/**
 * User preferences stored in localStorage
 * Key: 'fcp-user-preferences'
 */
export interface UserPreferences {
  /** Theme preference - NOTE: Also synced to 'fabric-cp-theme-mode' for ThemeProvider */
  theme: ThemeMode;

  /** Whether sidebar is collapsed (icon-only mode) */
  sidebarCollapsed: boolean;

  /** Default page to navigate to after login (null = Dashboard) */
  defaultLandingPage: LandingPage | null;

  /** Array of favorite items for quick access */
  favorites: FavoriteItem[];
}

/**
 * Default preferences for new users or when localStorage unavailable
 */
export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  theme: 'system',
  sidebarCollapsed: false,
  defaultLandingPage: null,
  favorites: [],
};

/**
 * User preferences context value
 */
export interface UserPreferencesContextValue {
  /** Current preferences */
  preferences: UserPreferences;

  /** Update a single preference */
  updatePreference: <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ) => void;

  /** Replace all preferences */
  setPreferences: (preferences: Partial<UserPreferences>) => void;

  /** Reset to defaults */
  resetPreferences: () => void;

  /** Add a page to favorites */
  addFavorite: (path: string, title?: string, category?: string) => void;

  /** Remove a page from favorites */
  removeFavorite: (path: string) => void;

  /** Check if a page is a favorite */
  isFavorite: (path: string) => boolean;

  /** Whether localStorage is available */
  isStorageAvailable: boolean;
}

// =============================================================================
// Search Types
// =============================================================================

/**
 * Types of resources that can appear in search results
 */
export type SearchableResourceType =
  | 'workspace'
  | 'lakehouse'
  | 'warehouse'
  | 'client'
  | 'storage_account'
  | 'policy'
  | 'capacity'
  | 'backup'
  | 'action'
  | 'restore'
  | 'alert'
  | 'analytics'
  | 'infrastructure'
  | 'settings'
  | 'page';

/**
 * Capacity types for displaying context badges
 */
export type CapacityType =
  | 'Reference' // Type 1
  | 'ClientDedicated' // Type 2
  | 'MultiTenant' // Type 3
  | 'Platform'; // Type 4

/**
 * Search result item returned from search operations
 */
export interface SearchResult {
  /** Unique identifier for the resource */
  id: string;

  /** Type of resource for icon/badge display */
  type: SearchableResourceType;

  /** Display name of the resource */
  name: string;

  /** Display title (alias for name) */
  title?: string;

  /** Capacity type for context (null for actions) */
  capacityType: CapacityType | null;

  /** Navigation path to the resource detail page */
  path: string;

  /** Category for grouping results */
  category?: string;

  /** Status of the resource (active, inactive, etc.) */
  status?: string;

  /** Last modified timestamp (ISO 8601) */
  lastModified: string | null;

  /** Optional description or subtitle */
  description?: string;

  /** Search relevance score (0-1, higher is better) */
  score?: number;

  /** Display icon (emoji or icon name) */
  icon?: string;

  /** Last updated timestamp for display (alias for lastModified) */
  lastUpdated?: string;

  /** Match type from search (label, description, path) */
  matchType?: 'label' | 'description' | 'path';
}

/**
 * Quick action available in command palette
 */
export interface QuickAction {
  /** Unique action identifier */
  id: string;

  /** Display label */
  label: string;

  /** Icon name (matches Icon component) */
  icon: string;

  /** Keyboard shortcut hint (e.g., "⌘K") */
  shortcut?: string;

  /** Description shown below label */
  description?: string;

  /** Action to execute (navigate to path or callback) */
  action: string | (() => void);
}

/**
 * Search state for the global search component
 */
export interface SearchState {
  /** Current search query */
  query: string;

  /** Search results */
  results: SearchResult[];

  /** Whether search is loading */
  isLoading: boolean;

  /** Whether command palette is open */
  isOpen: boolean;

  /** Currently selected result index */
  selectedIndex: number;
}

// =============================================================================
// Notification Types
// =============================================================================

/**
 * Notification severity levels (matches existing alert system)
 */
export type NotificationSeverity = 'info' | 'warning' | 'error' | 'critical';

/**
 * Notification item displayed in notification dropdown
 */
export interface Notification {
  /** Unique notification ID */
  id: string;

  /** Notification type/category */
  type: string;

  /** Severity level for icon/color coding */
  severity: NotificationSeverity;

  /** Notification message (may be truncated in UI) */
  message: string;

  /** Creation timestamp (ISO 8601) */
  timestamp: string;

  /** Whether notification has been read */
  read: boolean;

  /** Link to related resource (optional) */
  resourceLink?: string;

  /** Related resource name for context */
  resourceName?: string;

  /** Capacity type context for Type 1/4 alerts */
  capacityType?: CapacityType;
}

/**
 * Notification center state
 */
export interface NotificationState {
  /** Total unread notification count */
  unreadCount: number;

  /** Loaded notifications (last 24h or 20 items) */
  notifications: Notification[];

  /** Loading state */
  isLoading: boolean;

  /** Last fetch timestamp */
  lastFetched: string | null;
}

/**
 * useNotifications hook return type
 */
export interface NotificationsHook {
  /** Current notification state */
  state: NotificationState;

  /** Fetch/refresh notifications */
  refresh: () => Promise<void>;

  /** Mark a notification as read */
  markAsRead: (id: string) => Promise<void>;

  /** Mark all notifications as read */
  markAllAsRead: () => Promise<void>;

  /** Whether dropdown is open */
  isOpen: boolean;

  /** Set dropdown open state */
  setIsOpen: (open: boolean) => void;
}

// =============================================================================
// Breadcrumb Types
// =============================================================================

/**
 * Single breadcrumb segment
 * NOTE: Must be consistent with RoleBasedMenuFilter.ts Breadcrumb interface
 */
export interface Breadcrumb {
  /** Display label */
  label: string;

  /** Navigation path */
  path: string;

  /** Whether this is the current/active page */
  isActive: boolean;
}

/**
 * Extended breadcrumb for dynamic content
 */
export interface DynamicBreadcrumb extends Breadcrumb {
  /** Whether this segment is loading (e.g., fetching resource name) */
  isLoading?: boolean;

  /** Icon to display (optional) */
  icon?: string;
}

/**
 * Breadcrumb context for dynamic updates
 */
export interface BreadcrumbContextValue {
  /** Current breadcrumb trail */
  breadcrumbs: Breadcrumb[];

  /** Set custom breadcrumbs (for dynamic pages) */
  setBreadcrumbs: (breadcrumbs: Breadcrumb[]) => void;

  /** Append a segment to auto-generated breadcrumbs */
  appendBreadcrumb: (segment: Breadcrumb) => void;

  /** Reset to auto-generated breadcrumbs */
  resetBreadcrumbs: () => void;
}

// =============================================================================
// Sidebar/Navigation Types
// =============================================================================

/**
 * Sidebar collapse state
 */
export interface SidebarState {
  /** Whether sidebar is collapsed to icons only */
  isCollapsed: boolean;

  /** Toggle collapse state */
  toggle: () => void;

  /** Explicitly set collapse state */
  setCollapsed: (collapsed: boolean) => void;
}

/**
 * Mobile navigation state
 */
export interface MobileNavState {
  /** Whether mobile drawer is open */
  isOpen: boolean;

  /** Open the mobile drawer */
  open: () => void;

  /** Close the mobile drawer */
  close: () => void;

  /** Toggle mobile drawer */
  toggle: () => void;
}

// =============================================================================
// Keyboard Shortcuts Types
// =============================================================================

/**
 * Keyboard shortcut definition
 */
export interface KeyboardShortcut {
  /** Unique identifier */
  id: string;

  /** Key combination (e.g., 'ctrl+k', 'g d') */
  keys: string;

  /** Human-readable description */
  description: string;

  /** Handler function */
  handler: () => void;

  /** Whether shortcut is active in text inputs */
  enableInInputs?: boolean;

  /** Category for grouping in help modal */
  category?: string;
}

/**
 * useKeyboardShortcuts return type
 */
export interface KeyboardShortcutsHook {
  /** Register a shortcut */
  register: (shortcut: KeyboardShortcut) => void;

  /** Unregister a shortcut */
  unregister: (id: string) => void;

  /** Get all registered shortcuts */
  shortcuts: KeyboardShortcut[];

  /** Whether shortcuts overlay is visible */
  isHelpVisible: boolean;

  /** Toggle shortcuts help overlay */
  toggleHelp: () => void;

  /** Show shortcuts help overlay */
  showHelp: () => void;

  /** Hide shortcuts help overlay */
  hideHelp: () => void;
}

// =============================================================================
// API Response Types (for notifications)
// =============================================================================

/**
 * Alert response from /api/alerts
 */
export interface AlertResponse {
  id: number;
  severity: 'info' | 'warning' | 'error' | 'critical';
  message: string;
  timestamp: string;
  acknowledged: boolean;
  resource_type?: string;
  resource_id?: string;
  resource_name?: string;
  capacity_type?: string;
}

/**
 * Unread count response from /api/alerts/unread-count
 */
export interface UnreadCountResponse {
  count: number;
}

// =============================================================================
// Constants
// =============================================================================

/**
 * localStorage keys used by shell components
 */
export const STORAGE_KEYS = {
  /** Theme preference (shared with ThemeProvider) */
  THEME: 'fabric-cp-theme-mode',
  /** User preferences object */
  PREFERENCES: 'fcp-user-preferences',
  /** Recent navigation history */
  RECENT_NAVIGATION: 'fcp_recent_navigation',
} as const;

/**
 * CSS breakpoints for responsive design
 */
export const BREAKPOINTS = {
  /** Mobile breakpoint (below this = hamburger menu) */
  MOBILE: 768,
  /** Tablet breakpoint */
  TABLET: 1024,
  /** Desktop breakpoint */
  DESKTOP: 1440,
  /** Large desktop breakpoint */
  LARGE: 2560,
} as const;

/**
 * Default values for various shell settings
 */
export const SHELL_DEFAULTS = {
  /** Collapsed sidebar width in pixels */
  COLLAPSED_SIDEBAR_WIDTH: 64,
  /** Expanded sidebar width in pixels */
  EXPANDED_SIDEBAR_WIDTH: 256,
  /** Minimum touch target size in pixels (WCAG) */
  MIN_TOUCH_TARGET: 44,
  /** Search debounce delay in milliseconds */
  SEARCH_DEBOUNCE_MS: 300,
  /** Maximum search results to display */
  MAX_SEARCH_RESULTS: 10,
  /** Maximum notifications to display */
  MAX_NOTIFICATIONS: 20,
  /** Notification poll interval in milliseconds */
  NOTIFICATION_POLL_INTERVAL_MS: 60000,
  /** Maximum breadcrumb segments before collapsing */
  MAX_BREADCRUMB_SEGMENTS: 5,
} as const;
